// Type-only so this module never drags the cache slice (and its API/oidc
// dependency chain) into consumers' module graphs.
import type { CacheLookup } from "./app/cacheSlice";
import {
    AddressModel, AddressType, ContactType, OrganizationMemberModel, RoleModel, UserAddressModel,
    UserContactModel, UserDataModel, UserProfileDataModel
} from "./typings";

export const NewAddressModel: () => AddressModel = () => ({
    id: 0,
    street1: "",
    street2: "",
    city: "",
    state: "",
    postalCode: "",
    countryAlpha3Code: ""
});

export const NewUserAddressModel: () => UserAddressModel = () => ({
    id: 0,
    addressType: AddressType.Default,
    address: NewAddressModel()
});

export const NewUserProfileDataModel: () => UserProfileDataModel = () => ({
    userName: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    enabled: true,
    twoFactorEnabled: false,
    forcePasswordReset: false,
    welcomeEmailSent: false
});

export const NewUserContactModel: () => UserContactModel = () => ({
    id: 0,
    contactType: ContactType.Undefined,
    contact: "",
    phoneExtension: ""
});

export const NewUserDataModel: () => UserDataModel = () => ({
    userName: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    enabled: true,
    twoFactorEnabled: false,
    forcePasswordReset: false,
    welcomeEmailSent: false,
    userAddresses: [NewUserAddressModel()],
    contacts: [],
    userClients: [],
    roleIds: [],
    userOrganizations: []
});

export const NewOrganizationMemberModel: () => OrganizationMemberModel = () => ({
    organizationId: 0,
    organizationName: "",
    organizationDisplayName: "",
    roles: []
});

/**
 * Maps a `RoleSelector` id selection back to role objects, for the surfaces whose
 * state holds `RoleModel[]` rather than ids.
 *
 * Resolved against the catalogue first, then falling back to the object already in state.
 * That fallback is load-bearing: the API hides SuperAdmin from non-SuperAdmins, so state
 * can hold an id with no catalogue entry. Dropping it reads to the server as a removal,
 * which the SuperAdmin guard rejects with a 403 that blocks every later save.
 */
export const toRoleModels = (ids: number[], catalogue: CacheLookup[], current: RoleModel[]): RoleModel[] =>
    ids
        .map((id) => {
            const known = catalogue.find((r) => r.id === id);
            if (known) return { id: known.id, name: known.key, description: known.description };
            return current.find((r) => r.id === id);
        })
        .filter((r): r is RoleModel => Boolean(r));

export const ShortenStringIfLength = (string: string, length: number) => {
    return string.length > length ? (string.substring(0, length-1) + '...'): string
}


// An invited user has no name until they complete their profile, so a bare
// `${firstName} ${lastName}` renders "undefined undefined". The username is always set.
export const UserDisplayName = (user?: { firstName?: string, lastName?: string, userName?: string } | null): string => {
    if (!user) return '';
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return name || user.userName || '';
};
